import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateChildFn,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree
} from '@angular/router';
import { LanguageService } from '@shared/partner-ui/services/language.service';

export const languageRouteGuard: CanActivateFn = (route, state) => handleRoute(route, state);

export const languageRouteChildGuard: CanActivateChildFn = (route, state) => handleRoute(route, state);

function handleRoute(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
  const router = inject(Router);
  const language = inject(LanguageService);

  const rawLanguage = resolveLanguageParam(route);
  const normalizedLanguage = rawLanguage?.toLowerCase();
  const currentPath = normalizePath(state.url);

  if (normalizedLanguage && language.isSupportedLanguage(normalizedLanguage)) {
    language.setLanguage(normalizedLanguage);

    if (rawLanguage !== normalizedLanguage) {
      const suffix = currentPath.replace(/^\/[^/]+/, '');
      return buildRedirect(router, route, `/${normalizedLanguage}${suffix}`);
    }

    return true;
  }

  const fallbackLanguage = language.currentLanguage;

  if (rawLanguage) {
    const isLanguageLikeCode = /^[a-z]{2}$/i.test(rawLanguage);
    if (isLanguageLikeCode) {
      const suffix = currentPath.replace(/^\/[^/]+/, '');
      return buildRedirect(router, route, `/${fallbackLanguage}${suffix || ''}`);
    }

    return buildRedirect(router, route, `/${fallbackLanguage}${currentPath}`);
  }

  const targetPath = currentPath === '/' ? `/${fallbackLanguage}` : `/${fallbackLanguage}${currentPath}`;
  return buildRedirect(router, route, targetPath);
}

function buildRedirect(router: Router, route: ActivatedRouteSnapshot, path: string): UrlTree {
  const params = new URLSearchParams();
  for (const key of route.queryParamMap.keys) {
    for (const value of route.queryParamMap.getAll(key)) {
      params.append(key, value);
    }
  }

  const query = params.toString();
  const fragment = route.fragment ? `#${route.fragment}` : '';
  const url = `${path}${query ? `?${query}` : ''}${fragment}`;
  return router.parseUrl(url);
}

function normalizePath(url: string): string {
  const normalized = url.startsWith('/') ? url : `/${url}`;
  return normalized.split(/[?#]/, 1)[0] || '/';
}

function resolveLanguageParam(route: ActivatedRouteSnapshot): string | null {
  for (let index = route.pathFromRoot.length - 1; index >= 0; index -= 1) {
    const candidate = route.pathFromRoot[index].paramMap.get('lang');
    if (candidate) {
      return candidate;
    }
  }
  return null;
}
